/**
 * ThrowSystem — pure grab + throw logic.
 *
 * No Phaser, no DOM — fully unit-testable in Node.
 * Phaser wiring (visual position, syncZVisual) lives in Entity.ts.
 */
import type { ThrowType, ThrownState, ThrowerTeam, ThrowSpec } from '@/data/throws';
import type { HazardZoneDef } from '@/data/props';
import { damageProp, resolvePropDrop } from '@/systems/PropSystem';
import type { PropState, PropDrop } from '@/systems/PropSystem';

const THROW_GRAVITY = 0.5; // px/frame²

/**
 * Whether an attacker can initiate a grab on a target.
 *
 * @param attackerX       World X of the attacker
 * @param targetX         World X of the target
 * @param targetIsGrounded Target must be on the ground (not jumping/airborne)
 * @param targetIsStunned  Target is in hitstun
 * @param grabRange        Max horizontal distance for grab (px)
 * @param commandThrow     Heavy-hero override — skips the stunned requirement
 */
export function canGrab(
  attackerX: number,
  targetX: number,
  targetIsGrounded: boolean,
  targetIsStunned: boolean,
  grabRange: number,
  commandThrow: boolean,
): boolean {
  if (!targetIsGrounded) return false;
  if (!commandThrow && !targetIsStunned) return false;
  return Math.abs(attackerX - targetX) <= grabRange;
}

/**
 * Resolve throw direction from attacker facing and current directional input.
 *
 * - Overhead: up input (dy < 0)
 * - Back throw: horizontal input opposite to facing direction
 * - Forward throw: forward input or no input (default)
 */
export function resolveThrowType(facingDir: 1 | -1, inputDx: number, inputDy: number): ThrowType {
  if (inputDy < 0) return 'overhead';
  if (inputDx !== 0 && Math.sign(inputDx) !== facingDir) return 'back';
  return 'forward';
}

/** Launch velocities per throw type, in facing-normalised space. */
const THROW_VELOCITIES: Record<ThrowType, { vxMag: number; vy: number; forward: boolean }> = {
  forward: { vxMag: 5, vy: 5, forward: true },
  back: { vxMag: 6, vy: 4, forward: false },
  overhead: { vxMag: 1, vy: 10, forward: true },
};

/** Compute the initial horizontal and elevation velocities for a throw. */
export function computeThrowVelocity(
  type: ThrowType,
  facingDir: 1 | -1,
): { vx: number; vy: number } {
  const tv = THROW_VELOCITIES[type];
  const dir = tv.forward ? facingDir : -facingDir;
  return { vx: dir * tv.vxMag, vy: tv.vy };
}

/** Initialise a ThrownState for a victim being launched into the air. */
export function createThrownState(
  type: ThrowType,
  facingDir: 1 | -1,
  spec: ThrowSpec,
  throwerTeam: ThrowerTeam,
): ThrownState {
  const { vx, vy } = computeThrowVelocity(type, facingDir);
  return {
    vx,
    vy,
    elevation: 2, // start just off ground so the arc renders cleanly
    landingDamage: spec.landingDamage,
    flyDamage: spec.flyDamage,
    throwerTeam,
    alreadyHit: new Set(),
  };
}

/**
 * Advance a thrown arc by one frame. Mutates `state` in place.
 * @returns true = still airborne; false = entity has landed.
 */
export function tickThrownArc(state: ThrownState): boolean {
  state.elevation += state.vy;
  state.vy -= THROW_GRAVITY;
  if (state.elevation <= 0) {
    state.elevation = 0;
    state.vy = 0;
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Throw → environment collision resolution
// ---------------------------------------------------------------------------

/** Bonus damage dealt to the thrown entity when it hits a destructible prop. */
export const THROW_PROP_BONUS = 20;

export interface ThrowPropImpactResult {
  /** Bonus damage to apply to the thrown enemy on top of normal landing damage. */
  bonusDamage: number;
  /** Whether the prop was destroyed by this impact. */
  propDestroyed: boolean;
  /** Drop spawned by the destroyed prop (null if none or prop was already destroyed). */
  drop: PropDrop;
}

/**
 * Resolve what happens when a thrown enemy body hits a destructible prop.
 *
 * The prop is one-shot destroyed (thrown bodies carry enough momentum to
 * instantly wreck environment props). The thrown entity receives a bonus
 * damage hit on top of its normal landing damage.
 *
 * Returns zero bonus and no drop if the prop was already destroyed.
 */
export function resolveThrowPropImpact(prop: PropState): ThrowPropImpactResult {
  if (prop.destroyed) {
    return { bonusDamage: 0, propDestroyed: false, drop: null };
  }
  const propDestroyed = damageProp(prop, prop.health);
  const drop = propDestroyed ? resolvePropDrop(prop.def) : null;
  return { bonusDamage: THROW_PROP_BONUS, propDestroyed, drop };
}

export interface ThrowHazardImpactResult {
  /** Bonus damage to apply to the thrown entity on entering the hazard zone. */
  bonusDamage: number;
  /** When true, the entity is instantly KO'd regardless of remaining HP. */
  instantKO: boolean;
}

/**
 * Resolve what happens when a thrown enemy enters a stage hazard zone
 * (canal, third rail, acid vat). Hazard zones are permanent.
 */
export function resolveThrowHazardImpact(hazard: HazardZoneDef): ThrowHazardImpactResult {
  return {
    bonusDamage: hazard.throwBonusDamage,
    instantKO: hazard.instantKO ?? false,
  };
}

// ---------------------------------------------------------------------------
// Mid-flight body-to-body collision (throw → bystander enemy)
// ---------------------------------------------------------------------------

export interface FlightBodyCollisionResult {
  /** Damage applied to the bystander (= thrown state's flyDamage). */
  bystanderDamage: number;
  /** Bystander is knocked down by the impact. */
  knockdown: boolean;
}

/** Horizontal proximity radius (px) within which a thrown body can hit a bystander. */
export const FLIGHT_BODY_COLLISION_RADIUS = 32;

/**
 * Resolve a mid-flight collision between a thrown entity and a standing bystander.
 * The bystander takes flyDamage and is knocked down.
 * Each bystander can only be hit once per throw arc (tracked by alreadyHit).
 *
 * Returns null if out of range or already hit this arc.
 */
export function resolveFlightBodyCollision(
  thrown: ThrownState,
  bystanderCombatId: number,
  thrownX: number,
  bystanderX: number,
  proximityRadius = FLIGHT_BODY_COLLISION_RADIUS,
): FlightBodyCollisionResult | null {
  if (thrown.alreadyHit.has(bystanderCombatId)) return null;
  if (Math.abs(thrownX - bystanderX) > proximityRadius) return null;
  thrown.alreadyHit.add(bystanderCombatId);
  return { bystanderDamage: thrown.flyDamage, knockdown: true };
}

// ---------------------------------------------------------------------------
// Wall-bounce / screen-edge splat (SoR4 style)
// ---------------------------------------------------------------------------

export interface WallBounceResult {
  bounced: boolean;
  /** Frames of re-juggle window opened for follow-up attacks after a wall-splat. */
  rejuggleFrames: number;
}

/** Frames attackers have to extend a combo after a wall-splat (SoR4 re-juggle). */
export const WALL_BOUNCE_REJUGGLE_FRAMES = 20;

/**
 * Resolve a wall-bounce when a thrown entity reaches the stage horizontal boundary.
 * Reverses and attenuates horizontal velocity (×0.6 momentum loss) and opens a
 * short re-juggle window.
 *
 * Returns { bounced: false, rejuggleFrames: 0 } when still within bounds.
 */
export function resolveWallBounce(
  thrown: ThrownState,
  worldX: number,
  minX: number,
  maxX: number,
): WallBounceResult {
  if (worldX > minX && worldX < maxX) {
    return { bounced: false, rejuggleFrames: 0 };
  }
  thrown.vx = -thrown.vx * 0.6;
  return { bounced: true, rejuggleFrames: WALL_BOUNCE_REJUGGLE_FRAMES };
}

/**
 * Whether a standard mob enemy can initiate a grab on the player (MY-1961).
 *
 * Enemy grabs differ from player grabs: they do NOT require the player to be
 * in hitstun — only that the player is grounded and outside any invulnerability
 * window (e.g. dodge roll, wakeup invincibility).
 *
 * @param attackerX          World X of the grabbing enemy
 * @param targetX            World X of the player
 * @param targetIsGrounded   Player must be on the ground (not jumping/airborne)
 * @param targetIsInvulnerable  Player is currently invulnerable (no grab possible)
 * @param grabRange          Max horizontal distance to initiate the grab (px)
 */
export function canEnemyGrab(
  attackerX: number,
  targetX: number,
  targetIsGrounded: boolean,
  targetIsInvulnerable: boolean,
  grabRange: number,
): boolean {
  if (!targetIsGrounded) return false;
  if (targetIsInvulnerable) return false;
  return Math.abs(attackerX - targetX) <= grabRange;
}
